import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonSeverity } from 'primeng/button';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { StateVisitEnum } from 'src/app/enum/state-visit.enum';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { VisitModel } from 'src/app/models/preliminaries/visit.model';
import { ClientService } from 'src/app/services/archive/client.service';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { SellerService } from 'src/app/services/archive/seller.service';
import { ZoneService } from 'src/app/services/archive/zone.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { VisitService } from 'src/app/services/preliminaries/visit.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CancelScheduledVisitComponent } from '../cancel-scheduled-visit/cancel-scheduled-visit.component';

@Component({
  selector: 'app-register-scheduled-visit',
  templateUrl: './register-scheduled-visit.component.html',
  styleUrls: ['./register-scheduled-visit.component.scss'],
  standalone: false
})
export class RegisterScheduledVisitComponent implements OnInit {

  @ViewChild('cancelVisitModal') cancelVisitModalComponent?: CustomModalComponent | null = null;

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly clientService = inject(ClientService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly costCenterService = inject(CostCenterService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly headerService = inject(HeaderService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly sellerService = inject(SellerService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly visitService = inject(VisitService);
  private readonly windowService = inject(WindowService);
  private readonly zoneService = inject(ZoneService);

  branchId: number = 0;
  disabledForm: boolean = false;
  disableReverseState: boolean = false;
  disableState: boolean = false;
  formVisit: FormGroup = new FormGroup({});
  iconStateCancel: string = 'pi pi-times-circle';
  inputsForCancelVisitModals$ = new BehaviorSubject<any>({
    visitId: 0,
    disable: false
  });
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listClients: any[] = [];
  listCostCenters: any[] = [];
  listSellers: any[] = [];
  listQuotes: CustomListDropdown[] = [
    { label: 'Si', value: true },
    { label: 'No', value: false }
  ];
  listZones: any[] = [];
  nameWindow: string = 'Captura Visitas Programadas';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  severityCancel: ButtonSeverity = "danger";
  severityState: string = "danger";
  stateText: string = 'Pendiente';
  titleStateCancel: string = 'Cancelar Visita Programada';
  url: string = 'scheduled-visit';
  visitContract: VisitModel = new VisitModel();
  visitId: number = 0;
  componentCancelVisitModal = CancelScheduledVisitComponent;

  async ngOnInit() {
    try {
      this.validateForm(new VisitModel());
      this.headerService.setTitle(this.nameWindow);
      this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
      this.activatedRoute.params.subscribe(params => {
        this.visitId = Object.keys(params).length > 0 ? params?.id : null;
      });
      await this.getPermission();
      this.handleInitialState();
    } catch (error) {
      console.error('Error al inicializar el componente.', error);
    }
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async blockVisit(value: boolean) {
    if (!this.visitId || this.visitId === 0) {
      setTimeout(() => this.formVisit.controls['swBlock'].patchValue(!value));
      this.toastHandlerService.showToast('warning', 'Debe crear una visita programada.');
      return;
    }
    if (!this.permissionUpdatable) {
      setTimeout(() => this.formVisit.controls['swBlock'].patchValue(!value));
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para bloquear/desbloquear visitas programadas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    try {
      const hasPermission = await firstValueFrom(this.visitService.getPermission(OptionWindow.bloquear));
      if (!hasPermission) {
        setTimeout(() => this.formVisit.controls['swBlock'].patchValue(!value));
        this.toastHandlerService.showToast(
          'error',
          `El usuario no tiene permisos para bloquear/desbloquear visitas programadas. Comuníquese con el administrador del sistema.`
        );
        return;
      }
      const response = await firstValueFrom(this.visitService.blockVisit(this.visitId));
      this.visitContract = response;
      this.validateForm(this.visitContract);
      const successMessage = response.swBlock
        ? 'La visita programada fue bloqueada exitosamente.'
        : 'La visita programada fue desbloqueada exitosamente.';
      this.toastHandlerService.showToast('success', successMessage);
    } catch (error) {
      setTimeout(() => this.formVisit.controls['swBlock'].patchValue(!value));
      console.error('Error al bloquear la visita programada.', error);
    }
  }

  async changeBranch(branchId: number) {
    const branchChanged = this.branchId != branchId;
    if (!branchChanged) {
      return;
    }
    this.branchId = branchId;
    this.visitId = 0;
    this.visitContract = new VisitModel();
    this.visitContract.branchId = branchId;
    await this.getAllLists(true);
    this.validateForm(this.visitContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async changeClient(clientId: number) {
    if (clientId != 0 && clientId != null) {
      const clienName = this.listClients.find(x => x.value === clientId)?.label;
      if (clienName) {
        this.formVisit.get('clientVisit')?.patchValue(clienName);
      }
      this.formVisit.controls['clientVisit'].disable();
      await this.getCostCentersByClient(clientId);
      return;
    }
    this.formVisit.get('costCenterId')?.patchValue(0);
    await this.getCostCentersByClient(clientId);
  }

  async changeConsecutive(consecutive: number) {
    if (consecutive !== null && consecutive !== 0) {
      try {
        const response = await firstValueFrom(this.visitService.getVisitByKeysAsync(this.branchId, consecutive));
        this.visitContract = response;
        this.visitId = response.visitId;
        this.validateForm(this.visitContract);
        this.location.replaceState(`/${this.url}/register/${this.visitId}`);
      } catch (error) {
        console.error('Error al buscar la visita programada por llaves.', error);
        this.createNewRegister();
      }
    }
  }

  changeCostCenter(costCenterId: number) {
    if (costCenterId != 0 && costCenterId != null) {
      const costCenterName = this.listCostCenters.find(x => x.value === costCenterId)?.label;
      if (costCenterName) {
        this.formVisit.get('constructionSiteVisit')?.patchValue(costCenterName);
      }
      this.formVisit.controls['constructionSiteVisit'].disable();
      this.formVisit.get('swNewConstructionSite')?.patchValue(false);
      this.visitContract.swNewConstructionSite = false;
    }
    else {
      this.formVisit.get('constructionSiteVisit')?.patchValue(null);
      this.formVisit.controls['constructionSiteVisit'].enable();
      this.formVisit.get('swNewConstructionSite')?.patchValue(true);
      this.visitContract.swNewConstructionSite = true;
    }
  }

  changeConstructionSiteVisit(constructionSiteVisit: string) {
    if (constructionSiteVisit != null && constructionSiteVisit != '') {
      this.formVisit.get('swNewConstructionSite')?.patchValue(true);
      this.visitContract.swNewConstructionSite = true;
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear visitas programadas. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.visitId = 0;
    this.visitContract = new VisitModel();
    this.visitContract.branchId = this.branchId;
    this.visitContract.dateScheduled = new Date();
    this.visitContract.state = StateVisitEnum.Pending;
    this.listCostCenters = [];
    this.validateForm(this.visitContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar la visita programada. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.visitId || this.visitId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear una visita programada.'
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la visita programada?')) {
      try {
        await firstValueFrom(this.visitService.deleteVisit(this.visitContract.visitId));
        this.createNewRegister();
        this.toastHandlerService.showToast(
          'success',
          'La visita programada fue eliminada exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar la visita programada.', error);
      }
    }
  }

  async getAllLists(reload: boolean) {
    try {
      if (reload) {
        this.listSellers = await firstValueFrom(this.sellerService.getAllSellersForSelectByBranchIdAsync(this.branchId));
        return;
      }
      const [branches, zones, clients, sellers] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.zoneService.getAllZonesForSelectAsync()),
        firstValueFrom(this.clientService.getAllClientsForSelectAsync()),
        firstValueFrom(this.sellerService.getAllSellersForSelectByBranchIdAsync(this.branchId)),
      ]);
      this.listBranches = branches;
      this.listZones = zones;
      this.listClients = clients;
      this.listSellers = sellers;
    } catch (error) {
      console.error('Error al obtener las listas de visitas programadas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    let consecutive = this.formVisit.get("consecutive")?.value;
    const branchId = this.formVisit.get("branchId")?.value;
    if (branchId == null || branchId == 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione una sucursal.', 'Atención');
      return;
    }
    consecutive = consecutive === 0 ? null : consecutive;
    await this.handleOrderNavigation(buttonClick, branchId, consecutive);
  }

  async getCostCentersByClient(clientId: number) {
    if (!clientId) {
      this.listCostCenters = [];
      return;
    }
    try {
      this.listCostCenters = await firstValueFrom(this.costCenterService.getAllCostCentersForSelectByClientAsync(clientId, 0));
    } catch (error) {
      this.listCostCenters = [];
      console.error('Error al obtener los centros de costos de las visitas programadas.', error);
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const response = await firstValueFrom(
        this.userAuthenticatedService.branchObservable.pipe(filter(branch => !!branch && branch.branchId !== 0))
      );
      this.branchId = response.branchId;
    } catch (error) {
      console.error('Error al obtener la primera sucursal del usuario autenticado.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getFirstBranchByUserAuthenticatedAsync();
    await Promise.all([
      this.getPermissionProfileByUrl(),
      this.getAllLists(false),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getVisitById() {
    try {
      const response = await firstValueFrom(this.visitService.getVisitById(this.visitId));
      this.branchId = response.branchId;
      this.visitContract = response;
      await Promise.all([
        this.getAllLists(true),
        this.getCostCentersByClient(response.clientId as number)
      ]);
      this.validateForm(this.visitContract);
      this.location.replaceState(`/${this.url}/register/${this.visitId}`);
    } catch (error) {
      console.error('Error al obtener la visita por id.', error);
    }
  }

  goToBack(event: Event) {
    event.preventDefault();
    this.router.navigate([`/${this.url}`]);
  }

  async handleInitialState() {
    if (this.visitId !== null && this.visitId !== 0) {
      await this.getVisitById();
    } else {
      this.createNewRegister();
    }
  }

  private async handleOrderNavigation(buttonClick: any, branchId: number, consecutive: number) {
    const actionMap: Record<string, () => Promise<VisitModel>> = {
      first: () => firstValueFrom(this.visitService.getFirstVisitByKeysAsync(branchId)),
      next: () => firstValueFrom(this.visitService.getNextVisitByKeysAsync(branchId, consecutive)),
      previus: () => firstValueFrom(this.visitService.getPreviusVisitByKeysAsync(branchId, consecutive)),
      last: () => firstValueFrom(this.visitService.getLastVisitByKeysAsync(branchId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.visitContract = new VisitModel();
      this.visitContract.branchId = branchId;
      this.validateForm(this.visitContract);
      return;
    }
    try {
      const visit = await action();
      this.visitContract = visit;
      this.visitId = visit.visitId;
      this.validateForm(this.visitContract);
      this.location.replaceState(`/${this.url}/register/${this.visitId}`);
    } catch (error) {
      console.error(`Error al buscar la visita programada (${buttonClick.target.id}).`, error);
    }
  }

  async openCancelVisitModal() {
    const conceptId = this.visitContract.conceptId;
    if (!this.visitId && this.visitId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear una visita programada.', 'Atención');
      return;
    }
    if (conceptId) {
      this.openCancelModal(this.visitId, true);
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para modificar los estados de las visitas programadas.
      Comuníquese con el administrador del sistema.`, 'Atención');
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea cancelar la visita programada?')) {
      this.openCancelModal(this.visitId, false);
    }
  }

  private openCancelModal(visitId: number, disable: boolean) {
    this.inputsForCancelVisitModals$.next({
      visitId,
      disable
    });
    this.cancelVisitModalComponent?.show();
  }

  async reloadVisit(reload: boolean) {
    if (reload) {
      await this.getVisitById();
    }
  }

  async revertState() {
    if (this.permissionUpdatable) {
      try {
        this.visitContract.state = StateVisitEnum.Pending;
        const response = await firstValueFrom(this.visitService.changeState(this.visitContract));
        this.visitContract = response;
        this.validateForm(this.visitContract);
        this.toastHandlerService.showToast('success', 'El estado de la visita programada fue revertido exitosamente.');
      } catch (error) {
        console.error('Error al reversar el estado de la visita programada.', error);
      }
    } else {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para modificar el estado de visitas programadas.
      Comuníquese con el administrador del sistema.`, 'Atención');
    }
  }

  private sanitizeVisit(model: VisitModel): VisitModel {
    const visit: any = {};
    for (const key of Object.keys(this.formVisit.controls)) {
      visit[key] = model[key as keyof VisitModel];
    }
    visit.clientId = visit.clientId || null;
    visit.costCenterId = visit.costCenterId || null;
    visit.clientVisit = visit.clientVisit || null;
    visit.constructionSiteVisit = visit.constructionSiteVisit || null;
    visit.position = visit.position || null;
    visit.phone1 = visit.phone1 || null;
    visit.phone2 = visit.phone2 || null;
    visit.email = visit.email || null;
    visit.result = visit.result || null;
    visit.observationCancel = visit.observationCancel || null;
    visit.conceptId = visit.conceptId || null;
    visit.dateExecuted = visit.dateExecuted || null;
    return visit;
  }

  async send() {
    this.validateFormControls();
    if (this.formVisit.valid) {
      const modelVisit = this.sanitizeVisit(this.formVisit.getRawValue());

      if (modelVisit.clientId === null && modelVisit.clientVisit === null) {
        this.toastHandlerService.showToast('warning', 'El campo Cliente es obligatorio.', 'Atención');
        return;
      }
      if (modelVisit.costCenterId === null && modelVisit.constructionSiteVisit === null) {
        this.toastHandlerService.showToast('warning', 'El campo C.Costos es obligatorio.', 'Atención');
        return;
      }

      if (this.visitId == 0) {
        try {
          modelVisit.consecutive = 0;
          modelVisit.state = StateVisitEnum.Pending;
          const response = await firstValueFrom(this.visitService.createVisit(modelVisit));
          this.visitId = response.visitId;
          this.visitContract = response;
          this.validateForm(this.visitContract);
          this.location.replaceState(`/${this.url}/register/${this.visitId}`);
          this.toastHandlerService.showToast(
            'success',
            `La visita programada fue creada exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear la visita programada.', error);
        }
      } else {
        Object.assign(this.visitContract, modelVisit);
        try {
          const response = await firstValueFrom(this.visitService.updateVisit(this.visitContract));
          this.visitContract = response;
          this.validateForm(this.visitContract);
          this.toastHandlerService.showToast(
            'success',
            `La visita programada fue modificada exitosamente.`
          );
        } catch (error) {
          console.error('Error al modificar la visita programada.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      state: {
        required: 'El campo Estado es obligatorio.',
        min: 'Debe seleccionar un Estado válido.'
      },
      dateScheduled: {
        required: 'El campo Fecha Programada es obligatorio.'
      },
      clientVisit: {
        maxlength: 'El campo Cliente Visita no puede ser mayor a 70 caracteres.'
      },
      constructionSiteVisit: {
        maxlength: 'El campo C.Costos Visita no puede ser mayor a 70 caracteres.'
      },
      constructionSiteAddress: {
        required: 'El campo Dirección Obra es obligatorio.',
        maxlength: 'El campo Dirección Obra no puede ser mayor a 70 caracteres.'
      },
      zoneId: {
        required: 'El campo Zona es obligatorio.',
        min: 'Debe seleccionar una Zona válida.'
      },
      sellerId: {
        required: 'El campo Asesor es obligatorio.',
        min: 'Debe seleccionar un Asesor válido.'
      },
      contact: {
        required: 'El campo Contacto es obligatorio.',
        maxlength: 'El campo Contacto no puede ser mayor a 70 caracteres.'
      },
      position: {
        maxlength: 'El campo Cargo no puede ser mayor a 70 caracteres.'
      },
      phone1: {
        maxlength: 'El campo Teléfono 1 no puede ser mayor a 15 caracteres.'
      },
      phone2: {
        maxlength: 'El campo Teléfono 2 no puede ser mayor a 15 caracteres.'
      },
      initialGoal: {
        required: 'El campo Objetivo Inicial es obligatorio.',
        maxlength: 'El campo Objetivo Inicial no puede ser mayor a 1000 caracteres.'
      },
      result: {
        maxlength: 'El campo Resultado no puede ser mayor a 1000 caracteres.'
      },
      email: {
        email: 'El campo Email no es válido.',
        maxlength: 'El campo Email no puede ser mayor a 50 caracteres.'
      },
      observationCancel: {
        maxlength: 'El campo Observación Cancelación no puede ser mayor a 1000 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formVisit.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormState() {
    if (!this.permissionUpdatable) {
      this.disabledForm = true;
      this.disableReverseState = true;
      setTimeout(() => this.formVisit.disable());
    } else {
      this.disabledForm = false;
      setTimeout(() => this.formVisit.enable());
    }
    if (this.visitContract.swBlock) {
      setTimeout(() => this.formVisit.disable());
    } else {
      if (this.permissionUpdatable) {
        setTimeout(() => this.formVisit.enable());
      }
    }
    if (this.visitContract.state === StateVisitEnum.Canceled) {
      this.stateText = 'Cancelada';
      this.severityCancel = "primary";
      this.iconStateCancel = 'pi pi-eye';
      this.titleStateCancel = 'Ver observación de cancelación';
      this.disabledForm = true;
      this.disableReverseState = false;
      setTimeout(() => this.formVisit.disable());
    }
    if (this.visitContract.state === StateVisitEnum.Realized) {
      this.stateText = 'Realizada';
      this.severityState = "success";
      this.severityCancel = "danger";
      this.iconStateCancel = 'pi pi-times-circle';
      this.titleStateCancel = 'Cancelar Visita Programada';
      this.disabledForm = true;
      this.disableReverseState = true;
    }
    if (this.visitContract.state === StateVisitEnum.Pending) {
      this.stateText = 'Pendiente';
      this.severityState = "danger";
      this.severityCancel = "danger";
      this.iconStateCancel = 'pi pi-times-circle';
      this.titleStateCancel = 'Cancelar Visita Programada';
      this.disabledForm = false;
      this.disableReverseState = true;
    }
    setTimeout(() => { this.formControlStateService.setControlState(this.formVisit, ['dateExecuted', 'swQuote', 'result'], false) });
    setTimeout(() => { this.formControlStateService.setControlState(this.formVisit, ['branchId', 'swBlock'], true) });
    if (this.visitContract.consecutive === 0 || this.visitContract.consecutive === null) {
      setTimeout(() => { this.formControlStateService.setControlState(this.formVisit, ['consecutive'], false) });
    } else {
      setTimeout(() => { this.formControlStateService.setControlState(this.formVisit, ['consecutive'], true) });
    }
  }

  validateForm(visit: VisitModel) {
    this.formVisit = this.formBuilder.group({
      branchId: [visit.branchId, [Validators.required, Validators.min(1)]],
      consecutive: [{ value: visit.consecutive, disabled: true }],
      dateScheduled: [
        visit.dateScheduled ? this.dateConverterService.convertUtcToLocalDate(visit.dateScheduled) : new Date(),
        [Validators.required]
      ],
      dateExecuted: [
        visit.dateExecuted ? this.dateConverterService.convertUtcToLocalDate(visit.dateExecuted) : null
      ],
      clientVisit: [visit.clientVisit, [Validators.maxLength(70)]],
      constructionSiteVisit: [visit.constructionSiteVisit, [Validators.maxLength(70)]],
      clientId: [visit.clientId],
      costCenterId: [visit.costCenterId],
      constructionSiteAddress: [visit.constructionSiteAddress, [Validators.required, Validators.maxLength(70)]],
      zoneId: [visit.zoneId, [Validators.required, Validators.min(1)]],
      sellerId: [visit.sellerId, [Validators.required, Validators.min(1)]],
      contact: [visit.contact, [Validators.required, Validators.maxLength(70)]],
      position: [visit.position, [Validators.maxLength(70)]],
      phone1: [visit.phone1, [Validators.maxLength(15)]],
      phone2: [visit.phone2, [Validators.maxLength(15)]],
      swNewConstructionSite: [visit.swNewConstructionSite],
      initialGoal: [visit.initialGoal, [Validators.required, Validators.maxLength(1000)]],
      result: [visit.result, [Validators.maxLength(1000)]],
      swQuote: [visit.swQuote],
      swBlock: [visit.swBlock],
      email: [visit.email, [Validators.maxLength(50), Validators.email]],
      conceptId: [visit.conceptId],
      observationCancel: [visit.observationCancel, [Validators.maxLength(1000)]]
    });
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formVisit.controls).forEach((key) => {
      const control = this.formVisit.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
  }

}
